// src/api/client.js

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787/api';

console.log('API Client Configured:', { API_BASE_URL, hostname: window.location.hostname });

// Warn if on production but using localhost API
if (window.location.hostname !== 'localhost' && API_BASE_URL.includes('localhost')) {
    console.error('CRITICAL: Production frontend is trying to connect to Localhost API! Please set REACT_APP_API_URL.');
}

// Stateful mock data (persists in memory during session)
let mockCourses = [
    { id: 1, title: 'Photographic Memory 101', description: 'Unlock rapid recall.', track: 'Right Brain Intensive', instructor: 'Ms. Sarah', schedule_time: '2025-06-15T10:00:00', duration_minutes: 60, capacity: 10, price_credits: 1 },
    { id: 2, title: 'Spanish Immersion: Colors', description: 'Fun with colors.', track: 'Little Polyglot', instructor: 'Señor Rodriguez', schedule_time: '2025-06-16T11:00:00', duration_minutes: 60, capacity: 10, price_credits: 1 },
];

let mockPrograms = [
    { id: 1, title: 'Photographic Memory 101', description: 'Unlock rapid recall.', track: 'Right Brain Intensive', price_credits: 1 },
    { id: 2, title: 'Spanish Immersion', description: 'Learn Spanish naturally.', track: 'Little Polyglot', price_credits: 1 },
    { id: 3, title: 'Mindful Movers', description: 'Yoga for kids.', track: 'Heart & Harmony', price_credits: 1 }
];

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const fetchClasses = async () => {
    try {
        console.log(`Fetching: ${API_BASE_URL}/classes`);
        const response = await fetch(`${API_BASE_URL}/classes`);
        if (!response.ok) throw new Error('API Error');
        return await response.json();
    } catch (error) {
        console.error('Fetch failed:', error);
        console.warn('Backend unavailable, using mock data');
        await delay(300);
        return mockCourses;
    }
};

export const fetchPrograms = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/programs`);
        if (!response.ok) throw new Error('API Error');
        return await response.json();
    } catch (error) {
        return mockPrograms;
    }
};

export const createProgram = async (data) => {
    try {
        const response = await fetch(`${API_BASE_URL}/programs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        return await response.json();
    } catch (error) {
        console.warn('Using mock createProgram');
        const newProg = { ...data, id: Date.now() };
        mockPrograms.push(newProg);
        return { success: true, program: newProg };
    }
};

export const scheduleClass = async (data) => {
    try {
        const response = await fetch(`${API_BASE_URL}/classes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        return await response.json();
    } catch (error) {
        console.warn('Using mock scheduleClass');
        const newClass = { ...data, id: Date.now(), title: 'Mock Class' };
        mockCourses.push(newClass);
        return { success: true };
    }
};

export const deleteProgram = async (id) => {
    try {
        await fetch(`${API_BASE_URL}/programs/${id}`, { method: 'DELETE' });
    } catch (error) {
        console.warn('Using mock deleteProgram');
        mockPrograms = mockPrograms.filter(p => p.id !== id);
    }
};

export const deleteClass = async (id) => {
    try {
        await fetch(`${API_BASE_URL}/classes/${id}`, { method: 'DELETE' });
    } catch (error) {
        console.warn('Using mock deleteClass');
        mockCourses = mockCourses.filter(c => c.id !== id);
    }
};

export const bookClass = async (classId, userId, joinWaitlist = false) => {
    try {
        const token = localStorage.getItem('token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(`${API_BASE_URL}/bookings`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ class_id: classId, user_id: userId, join_waitlist: joinWaitlist })
        });

        if (!response.ok) {
            const err = await response.json();
            if (err.full) throw { message: err.error, full: true };
            throw new Error(err.error || 'Booking failed');
        }
        return await response.json();
    } catch (error) {
        if (error.full) throw error;
        console.warn('Backend unavailable/error, using mock booking');
        await delay(500);
        return { success: true, message: 'Mock Booking Confirmed', status: 'confirmed' };
    }
};

export const register = async (userData) => {
    // try/catch removed to debug API failure
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Registration failed');
    }
    return await response.json();
};

export const login = async (credentials) => {
    // try/catch removed to debug API failure
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Login failed');
    }
    return await response.json();
};

export const verifyEmail = async (token) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token })
        });
        if (!response.ok) throw new Error('Verification failed');
        return await response.json();
    } catch (error) {
        return { message: 'Mock Verification Success' };
    }
};

export const resendVerification = async (email) => {
    const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to resend email');
    }
    return await response.json();
};

export const forgotPassword = async (email) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        return await response.json();
    } catch (error) {
        return { message: 'Mock Reset Email Sent' };
    }
};

export const resetPassword = async (token, newPassword) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, newPassword })
        });
        if (!response.ok) throw new Error('Reset failed');
        return await response.json();
    } catch (error) {
        return { message: 'Mock Password Reset Success' };
    }
};

export const changePassword = async (currentPassword, newPassword) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not logged in');

    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
    });

    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to change password');
    }
    return await response.json();
};
